import { useState, useContext } from "react";
import {
  Container,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  InputAdornment,
  makeStyles,
} from "@material-ui/core";
import Axios from "axios";
import { useNavigate } from "react-router-dom";

import { UserContext } from "../../contexts/UserContext";
import { useToast } from "../../components/shared/Toast";

import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import LockIcon from "@material-ui/icons/Lock";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";

const useStyles = makeStyles((theme) => ({
  page: {
    position: "relative",
    minHeight: "100vh",
    background:
      "linear-gradient(160deg, #1a001a 0%, #2d002d 60%, #1a001a 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 500,
      height: 500,
      borderRadius: "50%",
      top: -180,
      right: -120,
      background:
        "radial-gradient(circle, rgba(199,125,199,0.32), transparent 70%)",
      filter: "blur(60px)",
      pointerEvents: "none",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      width: 460,
      height: 460,
      borderRadius: "50%",
      bottom: -180,
      left: -140,
      background:
        "radial-gradient(circle, rgba(155,59,155,0.28), transparent 70%)",
      filter: "blur(60px)",
      pointerEvents: "none",
    },
  },
  card: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: 420,
    background: "rgba(255,255,255,0.96)",
    borderRadius: 22,
    padding: "40px 36px 36px",
    boxShadow:
      "0 30px 60px rgba(76,0,76,0.4), 0 0 0 1px rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.4)",
    [theme.breakpoints.down("xs")]: {
      padding: "32px 22px 28px",
      borderRadius: 18,
    },
  },
  brandWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginBottom: 24,
  },
  brandLogo: {
    width: 38,
    height: 38,
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(76,0,76,0.18)",
  },
  brandLogoImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    border: "none",
    display: "block",
  },
  brandText: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#4c004c",
    letterSpacing: "-0.01em",
  },
  label: {
    fontSize: "0.62rem",
    fontWeight: 700,
    color: "#9b3b9b",
    textTransform: "uppercase",
    letterSpacing: "1.8px",
    textAlign: "center",
    marginBottom: 8,
  },
  title: {
    fontSize: "1.55rem",
    fontWeight: 700,
    textAlign: "center",
    marginBottom: 8,
    letterSpacing: "-0.01em",
    backgroundImage:
      "linear-gradient(90deg, #1a001a 0%, #4c004c 50%, #1a001a 100%)",
    backgroundSize: "200% auto",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
  },
  subtitle: {
    fontSize: "0.85rem",
    color: "#666",
    textAlign: "center",
    marginBottom: 28,
    lineHeight: 1.55,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "rgba(255,255,255,0.7)",
      fontSize: "0.88rem",
      transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
      "& fieldset": {
        borderColor: "rgba(76,0,76,0.14)",
        transition: "border-color 0.3s ease, border-width 0.3s ease",
      },
      "&:hover fieldset": {
        borderColor: "rgba(76,0,76,0.34)",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
        boxShadow: "0 0 0 4px rgba(155,59,155,0.1)",
        "& fieldset": {
          borderColor: "#4c004c",
          borderWidth: 1.5,
        },
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "13px 12px",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.85rem",
      color: "#888",
      "&.Mui-focused": { color: "#4c004c" },
    },
    "& .MuiInputAdornment-root svg": {
      color: "#9b3b9b",
      fontSize: 20,
      transition: "color 0.3s ease",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiInputAdornment-root svg": {
      color: "#4c004c",
    },
  },
  submitBtn: {
    background: "linear-gradient(135deg, #4c004c 0%, #6b1b6b 100%)",
    color: "#ffffff",
    padding: "14px 28px",
    borderRadius: 14,
    fontSize: "0.9rem",
    fontWeight: 600,
    textTransform: "none",
    letterSpacing: "0.2px",
    width: "100%",
    boxShadow: "0 8px 22px rgba(76,0,76,0.28)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    marginTop: 10,
    "& .MuiButton-endIcon": {
      transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
      marginLeft: 10,
    },
    "&, &:hover, &:focus, &:active, &:visited": {
      color: "#ffffff",
    },
    "&:hover": {
      background: "linear-gradient(135deg, #6b1b6b 0%, #9b3b9b 100%)",
      transform: "translateY(-2px)",
      boxShadow: "0 14px 32px rgba(76,0,76,0.4)",
    },
    "&:hover .MuiButton-endIcon": {
      transform: "translateX(4px)",
    },
    "&:disabled": {
      background: "#cccccc",
      color: "#ffffff",
      boxShadow: "none",
    },
  },
  footnote: {
    marginTop: 18,
    textAlign: "center",
    fontSize: "0.7rem",
    color: "#999",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    "& svg": { fontSize: 13, color: "#9b3b9b" },
  },

  // ── OTP Dialog ──
  otpDialog: {
    "& .MuiDialog-paper": {
      borderRadius: 20,
      padding: "8px 4px",
      maxWidth: 380,
      background:
        "linear-gradient(160deg, #ffffff 0%, #fdfafd 60%, #f8eef8 100%)",
      boxShadow: "0 30px 60px rgba(76,0,76,0.32)",
    },
  },
  otpTitle: {
    "& .MuiTypography-root": {
      fontSize: "1.15rem",
      fontWeight: 700,
      color: "#4c004c",
      letterSpacing: "-0.01em",
    },
  },
  otpDesc: {
    fontSize: "0.82rem",
    color: "#666",
    lineHeight: 1.6,
    marginBottom: 4,
  },
  otpActions: {
    flexDirection: "column",
    alignItems: "stretch",
    padding: "8px 24px 22px",
    gap: 10,
  },
}));

export const Login = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);
  const toast = useToast();

  const [disableButton, setDisableButton] = useState(false);
  const [uname, setUname] = useState("");
  const [pass, setPass] = useState("");
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState(0);
  const [open, setOpen] = useState(false);

  const handleClose = () => setOpen(false);

  const login = (event) => {
    event.preventDefault();
    if (uname === "") {
      toast.error("Please enter admin username.");
      setErr(1);
    } else if (pass === "") {
      toast.error("Please enter admin password.");
      setErr(2);
    } else {
      setDisableButton(true);
      setErr(0);
      const formData = new FormData();
      formData.append("uname", uname);
      formData.append("pass", pass);
      toast.info("Logging in, please wait...");
      Axios.post("/api/addlogin", formData, {
        headers: { headKey: headKey },
      }).then((res) => {
        if (res.data.status) {
          toast.success(res.data.msg);
          setDisableButton(false);
          setOpen(true);
        } else {
          setDisableButton(false);
          toast.error(res.data.msg);
        }
      });
    }
  };

  const submitOtp = () => {
    if (otp === "") {
      toast.error("Please enter the OTP.");
      setErr(3);
    } else {
      setErr(0);
      toast.info("Validating OTP, please wait...");
      const formData = new FormData();
      formData.append("uname", uname);
      formData.append("pass", pass);
      formData.append("aotp", otp);
      Axios.post("/api/addlogin", formData, {
        headers: { headKey: headKey },
      }).then((res) => {
        if (res.data.status) {
          setOpen(false);
          setUser(res.data.userdata);
          toast.success(res.data.msg);
          navigate("/dash");
        } else {
          toast.error(res.data.msg);
        }
      });
    }
  };

  return (
    <Box className={classes.page}>
      <Container maxWidth={false} disableGutters>
        <Box className={classes.card} margin="0 auto">
          <Box className={classes.brandWrap}>
            <Box className={classes.brandLogo}>
              <img
                src="/static/logo.png"
                alt="Arawinz"
                className={classes.brandLogoImg}
              />
            </Box>
            <Typography className={classes.brandText}>
              Arawinz Soft Solutions
            </Typography>
          </Box>

          <Typography className={classes.label}>Admin Portal</Typography>
          <Typography className={classes.title}>Welcome back</Typography>
          <Typography className={classes.subtitle}>
            Sign in to access the dashboard.
          </Typography>

          <form className={classes.form} onSubmit={login}>
            <TextField
              label="Username"
              variant="outlined"
              error={err === 1}
              value={uname}
              onChange={(e) => setUname(e.target.value)}
              required
              fullWidth
              className={classes.textField}
              InputLabelProps={{ shrink: true }}
              placeholder="admin"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Password"
              variant="outlined"
              type="password"
              error={err === 2}
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              required
              fullWidth
              className={classes.textField}
              InputLabelProps={{ shrink: true }}
              placeholder="••••••••"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              disabled={disableButton}
              className={classes.submitBtn}
              endIcon={!disableButton && <ArrowForwardIcon />}
            >
              {disableButton ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <Typography className={classes.footnote}>
            <LockIcon /> Authorized personnel only
          </Typography>
        </Box>
      </Container>

      <Dialog
        open={open}
        onClose={handleClose}
        className={classes.otpDialog}
      >
        <DialogTitle className={classes.otpTitle}>
          Enter verification code
        </DialogTitle>
        <DialogContent>
          <DialogContentText className={classes.otpDesc}>
            We've sent a one-time password to your registered contact. Enter it
            below to continue.
          </DialogContentText>
          <Box style={{ marginTop: 14 }}>
            <TextField
              label="OTP"
              variant="outlined"
              error={err === 3}
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              fullWidth
              autoFocus
              className={classes.textField}
              InputLabelProps={{ shrink: true }}
              placeholder="6-digit code"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKeyOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions className={classes.otpActions}>
          <Button
            onClick={submitOtp}
            className={classes.submitBtn}
            endIcon={<ArrowForwardIcon />}
          >
            Verify &amp; sign in
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
